import React from "react";
import { Box, Container, Heading, Text, SimpleGrid, VStack, HStack, Icon, Badge, Button, List, ListItem, ListIcon } from "@chakra-ui/react";
import { FaStar, FaCheckCircle, FaCrown, FaChartBar, FaUserTie, FaFileInvoice } from "react-icons/fa";
import { NavbarWithCallToAction } from "../../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../../../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import { SEOHead } from "../../../components/SEOHead";

const NAVY = "#141C49";
const TEAL = "#0D7F6E";
const SAND = "#C2A878";
const OFF_WHITE = "#F0EBE0";

const tiers = [
  {
    name: "Essential",
    price: "$2,995",
    highlight: false,
    accentColor: SAND,
    features: ["Property search", "Market analysis", "Finance intro", "Basic support"]
  },
  {
    name: "Premium",
    price: "$5,995",
    highlight: true,
    accentColor: TEAL,
    features: ["Everything in Essential", "Property inspections", "Negotiation support", "Settlement coordination", "6 months support"]
  },
  {
    name: "Elite",
    price: "$9,995",
    highlight: false,
    accentColor: NAVY,
    features: ["Everything in Premium", "Portfolio strategy", "Tax structuring advice", "Dedicated account manager", "12 months VIP support"]
  }
];

const PropertyServicesOPAPlus: React.FC = () => {
  return (
    <Box minH="100vh" bg="white">
      <SEOHead
        pagePath="/property/services/opa-plus"
        fallbackTitle="OPA+ Premium Services – One Property Australia"
        fallbackDescription="Comprehensive property analysis and optimisation services for serious investors. Essential, Premium, and Elite packages available."
      />
      <NavbarWithCallToAction />

      <Box bg={NAVY} color="white" py={20}>
        <Container maxW="container.xl">
          <VStack spacing={6} textAlign="center">
            <HStack>
              <Icon as={FaCrown} boxSize={8} />
              <Badge bg={SAND} color={NAVY} px={4} py={2} fontSize="md" borderRadius="full">PREMIUM</Badge>
            </HStack>
            <Heading size="3xl" fontWeight="black">OPA+ Premium Services</Heading>
            <Text fontSize="xl" maxW="3xl">Comprehensive property analysis and optimisation for serious investors</Text>
          </VStack>
        </Container>
      </Box>

      <Container maxW="container.xl" py={16}>
        <VStack spacing={4} textAlign="center" mb={12}>
          <Heading size="xl">Choose Your Service Level</Heading>
          <Text fontSize="lg" color="gray.600">Flexible packages to match your investment needs</Text>
        </VStack>

        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={8}>
          {tiers.map((tier) => (
            <Box
              key={tier.name}
              bg={tier.highlight ? OFF_WHITE : "white"}
              border="2px solid"
              borderColor={tier.highlight ? TEAL : "gray.200"}
              borderRadius="2xl"
              p={8}
              position="relative"
              transform={tier.highlight ? "scale(1.05)" : "none"}
              boxShadow={tier.highlight ? "2xl" : "lg"}
            >
              {tier.highlight && (
                <Badge position="absolute" top={-3} right={-3} bg={TEAL} color="white" px={4} py={2} borderRadius="full">
                  POPULAR
                </Badge>
              )}
              <VStack spacing={6} align="stretch">
                <VStack spacing={2}>
                  <Heading size="lg" color={tier.accentColor}>{tier.name}</Heading>
                  <Heading size="2xl" color="gray.800">{tier.price}</Heading>
                  <Text fontSize="sm" color="gray.600">One-time fee</Text>
                </VStack>
                <List spacing={3}>
                  {tier.features.map((feature, i) => (
                    <ListItem key={i} display="flex" alignItems="center">
                      <ListIcon as={FaCheckCircle} color={tier.accentColor} />
                      <Text fontSize="sm">{feature}</Text>
                    </ListItem>
                  ))}
                </List>
                <Button
                  size="lg"
                  mt={4}
                  bg={tier.highlight ? TEAL : tier.accentColor === NAVY ? NAVY : SAND}
                  color="white"
                  _hover={{ opacity: 0.9 }}
                >
                  Select {tier.name}
                </Button>
              </VStack>
            </Box>
          ))}
        </SimpleGrid>
      </Container>

      <Box bg={TEAL} color="white" py={16}>
        <Container maxW="container.xl">
          <VStack spacing={6} textAlign="center">
            <Icon as={FaCrown} boxSize={16} />
            <Heading size="xl">Upgrade Your Investment Strategy</Heading>
            <Text fontSize="lg">Schedule a call to discuss the right package for you</Text>
          </VStack>
        </Container>
      </Box>

      <FooterWithFourColumns />
    </Box>
  );
};

export default PropertyServicesOPAPlus;
